import { applyPromotion } from "@/utils/functions/applySinglePromotion"
import {
  AllTypeService,
  BookingId,
  BookingServiceItem,
  BookingTable,
  DiscountAppliedGlobal,
  FlowType,
  KeysUsed,
  PromotionTable,
  PromotionType,
  Service,
} from "@okeys/shared-utils"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

// ============================================================================
// Types pour le parcours de paiement (local à l'app, non stocké en DynamoDB)
// ============================================================================

/**
 * État du parcours de paiement - indique l'étape actuelle
 */
export type CollectionState =
  | "choiceAppointment" // Sélection du rendez-vous (withBooking)
  | "validateService" // Validation des services effectués
  | "validatePayment" // Validation et saisie du paiement
  | "validate" // Confirmation finale

/**
 * Méthode de paiement
 */
export type PaymentMethod = "cash" | "card" | "mixed"

/**
 * Information sur un paiement effectué (pour paiements mixtes/progressifs)
 */
export type CompletedPayment = {
  id: string // Identifiant unique du paiement
  type: "cash" | "card"
  amount: number // Montant en centimes
  timestamp: number // Timestamp de création
  paymentIntentId?: string // ID du PaymentIntent Stripe (pour paiements carte uniquement)
}

/**
 * Progression d'un paiement (pour paiements mixtes/progressifs)
 */
export type PaymentProgress = {
  totalAmount: number // Montant total à payer
  remainingAmount: number // Montant restant à payer
  completedPayments: CompletedPayment[] // Paiements déjà effectués
  currentCashInput: string // Input actuel pour espèces
  currentCardInput: string // Input actuel pour carte
}

/**
 * Information de paiement en cours
 */
export type PaymentInfo = {
  method: PaymentMethod
  amount?: number // Montant si déjà calculé
}

/**
 * Information complète du parcours de paiement (état local Redux)
 */
export type CollectionInfo = {
  collectionState: CollectionState // Étape actuelle du parcours
  services: BookingServiceItem[] // Services sélectionnés/effectués
  serviceWithPromo: BookingServiceItem[] // Services avec promotions appliquées
  keysUsed: KeysUsed[] // Clés de fidélité utilisées
  discountAppliedGlobal: DiscountAppliedGlobal<"booking"> | null // Remise globale appliquée
  selectedClientId?: string // ID du client sélectionné
  newServicePrestatereId?: string // ID du prestataire pour nouveaux services
  paymentInfo?: PaymentInfo // Information du paiement en cours
  paymentProgress?: PaymentProgress // Progression du paiement (mixte/progressif)
}

export type HistoricInfo = {
  booking: BookingTable
}

export type CashRegisterState = {
  collectionInfo?: CollectionInfo
  historicInfo?: HistoricInfo
  flowType: FlowType
  bookingId?: string
  addServiceSectionInfo?: {
    selectedCategory?: string
    selectedServiceId?: string
  }
}

const initialState: CashRegisterState = {
  collectionInfo: undefined,
  historicInfo: undefined,
  flowType: "withBooking",
  bookingId: undefined,
  addServiceSectionInfo: {
    selectedCategory: undefined,
    selectedServiceId: undefined,
  },
}

export const cashRegisterSlice = createSlice({
  name: "cashRegister",
  initialState,
  reducers: {
    initializeCollectionInfo: (
      state,
      action: PayloadAction<CollectionState | undefined>
    ) => {
      state.collectionInfo = {
        collectionState: action.payload || "choiceAppointment",
        services: [],
        serviceWithPromo: [],
        keysUsed: [],
        discountAppliedGlobal: null,
      }
    },
    setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
      if (state.collectionInfo) {
        state.collectionInfo.paymentInfo = {
          method: action.payload,
        }
      }
    },
    setCollectionInfo: (state, action: PayloadAction<CollectionInfo>) => {
      state.collectionInfo = action.payload
    },
    setWithAppointmentStep: (state, action: PayloadAction<CollectionState>) => {
      if (state.collectionInfo) {
        state.collectionInfo.collectionState = action.payload
      }
    },
    setBookingId: (state, action: PayloadAction<BookingId>) => {
      if (state.collectionInfo) {
        state.bookingId = action.payload
      }
    },
    clearBookingId: (state) => {
      if (state.collectionInfo) {
        state.bookingId = undefined
      }
    },
    addServiceItem: (
      state,
      action: PayloadAction<{
        serviceItem: BookingServiceItem
        promotions: PromotionTable<PromotionType>[]
        availableServices: Service<AllTypeService>[]
      }>
    ) => {
      const { serviceItem, promotions, availableServices } = action.payload

      if (state.collectionInfo) {
        state.collectionInfo.services.push(serviceItem)
        const serviceWithPromo = applyPromotion(
          promotions,
          state.collectionInfo.services,
          state.collectionInfo.keysUsed,
          availableServices,
          state.collectionInfo.selectedClientId
        )
        state.collectionInfo.serviceWithPromo = serviceWithPromo
      }
    },
    removeServiceItem: (
      state,
      action: PayloadAction<{
        serviceId: string
        promotions: PromotionTable<PromotionType>[]
        availableServices: Service<AllTypeService>[]
      }>
    ) => {
      const { serviceId, promotions, availableServices } = action.payload

      if (state.collectionInfo) {
        // Trouver l'index du premier service avec cet ID
        const serviceIndex = state.collectionInfo.services.findIndex(
          (service) => service.serviceId === serviceId
        )

        // Supprimer une seule occurrence du service
        if (serviceIndex !== -1) {
          state.collectionInfo.services.splice(serviceIndex, 1)
        }

        const numberService = state.collectionInfo.services.filter(
          (service) => service.serviceId === serviceId
        ).length

        const keysUsedIndexForService = state.collectionInfo.keysUsed.findIndex(
          (key) => key.serviceId === serviceId
        )
        if (keysUsedIndexForService !== -1) {
          if (
            numberService <
            state.collectionInfo.keysUsed[keysUsedIndexForService]
              .numberItemWithKeysUsed
          ) {
            state.collectionInfo.keysUsed[
              keysUsedIndexForService
            ].numberItemWithKeysUsed = numberService
          }
        }

        // Mettre à jour les services avec promotions
        const serviceWithPromo = applyPromotion(
          promotions,
          state.collectionInfo.services,
          state.collectionInfo.keysUsed,
          availableServices,
          state.collectionInfo.selectedClientId
        )
        state.collectionInfo.serviceWithPromo = serviceWithPromo
      }
    },
    updateServiceItemPrice: (
      state,
      action: PayloadAction<{
        serviceId: string
        newPrice: number
        flowType: FlowType
      }>
    ) => {
      const { serviceId, newPrice } = action.payload

      if (state.collectionInfo) {
        const service = state.collectionInfo.services.find(
          (s) => s.serviceId === serviceId
        )
        if (service) {
          service.price = newPrice
        }
      }
    },
    toggleServiceCancelled: (
      state,
      action: PayloadAction<{
        serviceId: string
        promotions: PromotionTable<PromotionType>[]
        availableServices: Service<AllTypeService>[]
      }>
    ) => {
      const { serviceId, promotions, availableServices } = action.payload

      if (state.collectionInfo) {
        const serviceWithPromo = applyPromotion(
          promotions,
          state.collectionInfo.services,
          state.collectionInfo.keysUsed,
          availableServices,
          state.collectionInfo.selectedClientId
        )
        state.collectionInfo.serviceWithPromo = serviceWithPromo
        const service = state.collectionInfo.services.find(
          (s) => s.serviceId === serviceId
        )
        if (service) {
          service.cancelled = !service.cancelled
        }
      }
    },
    updateServiceCancelled: (
      state,
      action: PayloadAction<{
        serviceIds: string
        count: number
      }>
    ) => {
      const { serviceIds, count } = action.payload

      if (state.collectionInfo) {
        const services = state.collectionInfo.services.filter(
          (s) => s.serviceId === serviceIds
        )
        const serviceWithPromo = state.collectionInfo.serviceWithPromo.filter(
          (s) => s.serviceId === serviceIds
        )
        for (const service of serviceWithPromo) {
          service.cancelled = false
        }
        for (const service of services) {
          service.cancelled = false
        }
        for (let i = 0; i < count; i++) {
          services[i].cancelled = true
        }
        for (let i = 0; i < count; i++) {
          serviceWithPromo[i].cancelled = true
        }
      }
    },
    nextWithAppointmentStep: (state) => {
      if (state.collectionInfo) {
        const currentState = state.collectionInfo.collectionState
        const steps: CollectionState[] = [
          "choiceAppointment",
          "validateService",
          "validatePayment",
          "validate",
        ]
        const currentIndex = steps.indexOf(currentState)
        if (currentIndex < steps.length - 1) {
          state.collectionInfo.collectionState = steps[currentIndex + 1]
        }
      }
    },
    previousWithAppointmentStep: (state) => {
      if (state.collectionInfo) {
        const currentState = state.collectionInfo.collectionState
        const steps: CollectionState[] = [
          "choiceAppointment",
          "validateService",
          "validatePayment",
          "validate",
        ]
        const currentIndex = steps.indexOf(currentState)
        if (currentIndex > 0) {
          state.collectionInfo.collectionState = steps[currentIndex - 1]
        }
      }
    },
    resetWithAppointmentInfo: (state) => {
      state.collectionInfo = undefined
    },
    setSelectedCategory: (state, action: PayloadAction<string | undefined>) => {
      if (state.addServiceSectionInfo) {
        state.addServiceSectionInfo.selectedCategory = action.payload
      }
    },
    setSelectedServiceId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      if (state.addServiceSectionInfo) {
        state.addServiceSectionInfo.selectedServiceId = action.payload
      }
    },
    setNewServicePrestatereId: (state, action: PayloadAction<string>) => {
      if (state.collectionInfo) {
        state.collectionInfo.newServicePrestatereId = action.payload
      }
    },
    setPaymentInfo: (state, action: PayloadAction<PaymentInfo>) => {
      if (state.collectionInfo) {
        state.collectionInfo.paymentInfo = action.payload
      }
    },
    clearPaymentInfo: (state) => {
      if (state.collectionInfo) {
        state.collectionInfo.paymentInfo = undefined
      }
    },
    // Nouvelles actions pour la gestion des paiements progressifs
    initializePaymentProgress: (state, action: PayloadAction<number>) => {
      if (state.collectionInfo) {
        state.collectionInfo.paymentProgress = {
          totalAmount: action.payload,
          remainingAmount: action.payload,
          completedPayments: [],
          currentCashInput: "",
          currentCardInput: "",
        }
      }
    },
    updateCashInput: (state, action: PayloadAction<string>) => {
      if (state.collectionInfo?.paymentProgress) {
        state.collectionInfo.paymentProgress.currentCashInput = action.payload
      }
    },
    updateCardInput: (state, action: PayloadAction<string>) => {
      if (state.collectionInfo?.paymentProgress) {
        state.collectionInfo.paymentProgress.currentCardInput = action.payload
      }
    },
    addCompletedPayment: (state, action: PayloadAction<CompletedPayment>) => {
      if (state.collectionInfo?.paymentProgress) {
        state.collectionInfo.paymentProgress.completedPayments.push(
          action.payload
        )
        state.collectionInfo.paymentProgress.remainingAmount -=
          action.payload.amount

        // Réinitialiser l'input correspondant au type de paiement
        if (action.payload.type === "cash") {
          state.collectionInfo.paymentProgress.currentCashInput = ""
        } else if (action.payload.type === "card") {
          state.collectionInfo.paymentProgress.currentCardInput = ""
        }
      }
    },
    removeCompletedPayment: (state, action: PayloadAction<string>) => {
      if (state.collectionInfo?.paymentProgress) {
        const paymentToRemove =
          state.collectionInfo.paymentProgress.completedPayments.find(
            (p) => p.id === action.payload
          )

        if (paymentToRemove) {
          state.collectionInfo.paymentProgress.completedPayments =
            state.collectionInfo.paymentProgress.completedPayments.filter(
              (p) => p.id !== action.payload
            )
          state.collectionInfo.paymentProgress.remainingAmount +=
            paymentToRemove.amount
        }
      }
    },
    resetPaymentProgress: (state) => {
      if (state.collectionInfo) {
        state.collectionInfo.paymentProgress = undefined
      }
    },
    // Actions pour withoutAppointment
    setWithoutAppointmentStep: (
      state,
      action: PayloadAction<CollectionState>
    ) => {
      if (state.collectionInfo) {
        state.collectionInfo.collectionState = action.payload
      }
    },
    nextWithoutAppointmentStep: (state) => {
      if (state.collectionInfo) {
        const currentState = state.collectionInfo.collectionState
        const steps: CollectionState[] = [
          "validateService",
          "validatePayment",
          "validate",
        ]
        const currentIndex = steps.indexOf(currentState)
        if (currentIndex < steps.length - 1) {
          state.collectionInfo.collectionState = steps[currentIndex + 1]
        }
      }
    },
    previousWithoutAppointmentStep: (state) => {
      if (state.collectionInfo) {
        const currentState = state.collectionInfo.collectionState
        const steps: CollectionState[] = [
          "validateService",
          "validatePayment",
          "validate",
        ]
        const currentIndex = steps.indexOf(currentState)
        if (currentIndex > 0) {
          state.collectionInfo.collectionState = steps[currentIndex - 1]
        }
      }
    },
    setFlowType: (state, action: PayloadAction<FlowType>) => {
      state.flowType = action.payload
    },
    resetFlowType: (state) => {
      state.flowType = "withBooking"
    },
    restoreFromLocalStorage: (
      state,
      action: PayloadAction<Partial<CashRegisterState>>
    ) => {
      // Restaurer l'état depuis localStorage
      if (action.payload.collectionInfo) {
        state.collectionInfo = action.payload.collectionInfo
      }
      if (action.payload.bookingId !== undefined) {
        state.bookingId = action.payload.bookingId
      }
      if (action.payload.flowType) {
        const ft = action.payload.flowType
        state.flowType = (
          ft === "withAppointment"
            ? "withBooking"
            : ft === "withoutAppointment"
            ? "withoutBooking"
            : ft
        ) as FlowType
      }
    },
    setSelectedClientId: (state, action: PayloadAction<string | undefined>) => {
      if (state.collectionInfo) {
        state.collectionInfo.selectedClientId = action.payload
      }
    },
    setKeysUsed: (
      state,
      action: PayloadAction<
        {
          promotions: PromotionTable<PromotionType>[]
          availableServices: Service<AllTypeService>[]
        } & KeysUsed
      >
    ) => {
      if (state.collectionInfo) {
        const {
          serviceId,
          numberItemWithKeysUsed,
          promotions,
          availableServices,
        } = action.payload
        const isAlreadyUsed = state.collectionInfo.keysUsed.find(
          (key) => key.serviceId === serviceId
        )
        if (!isAlreadyUsed) {
          if (numberItemWithKeysUsed > 0) {
            state.collectionInfo.keysUsed.push({
              serviceId,
              numberItemWithKeysUsed,
            })
          } else {
            return
          }
        } else {
          if (numberItemWithKeysUsed > 0) {
            isAlreadyUsed.numberItemWithKeysUsed =
              action.payload.numberItemWithKeysUsed
          } else {
            state.collectionInfo.keysUsed =
              state.collectionInfo.keysUsed.filter(
                (key) => key.serviceId !== serviceId
              )
          }
        }
        const serviceWithPromo = applyPromotion(
          promotions,
          state.collectionInfo.services,
          state.collectionInfo.keysUsed,
          availableServices,
          state.collectionInfo.selectedClientId
        )
        state.collectionInfo.serviceWithPromo = serviceWithPromo
      }
    },
    removeKeysUsed: (state, action: PayloadAction<string>) => {
      if (state.collectionInfo) {
        state.collectionInfo.keysUsed = state.collectionInfo.keysUsed.filter(
          (key) => key.serviceId !== action.payload
        )
      }
    },
  },
})

export const {
  initializeCollectionInfo,
  setPaymentMethod,
  setCollectionInfo,
  setWithAppointmentStep,
  setBookingId,
  clearBookingId,
  addServiceItem,
  removeServiceItem,
  updateServiceItemPrice,
  toggleServiceCancelled,
  nextWithAppointmentStep,
  previousWithAppointmentStep,
  resetWithAppointmentInfo,
  setSelectedCategory,
  setSelectedServiceId,
  setNewServicePrestatereId,
  setPaymentInfo,
  clearPaymentInfo,
  initializePaymentProgress,
  updateCashInput,
  updateCardInput,
  addCompletedPayment,
  removeCompletedPayment,
  resetPaymentProgress,
  // Actions pour withoutAppointment
  setWithoutAppointmentStep,
  nextWithoutAppointmentStep,
  previousWithoutAppointmentStep,
  setFlowType,
  resetFlowType,
  restoreFromLocalStorage,
  setSelectedClientId,
  setKeysUsed,
  removeKeysUsed,
  updateServiceCancelled,
} = cashRegisterSlice.actions
export default cashRegisterSlice.reducer
